<template>
  <!-- Uno spicchio solo non è un grafico: un cerchio pieno al 100% non dice
       niente che la frase non dica meglio. -->
  <p v-if="spicchi.length === 1" class="torta-unica">
    Tutto in <strong>{{ spicchi[0].etichetta }}</strong
    >: {{ formatta(spicchi[0].valore) }}.
  </p>

  <div v-else class="torta">
    <svg
      class="torta-disegno"
      viewBox="0 0 120 120"
      role="img"
      :aria-label="descrizione"
    >
      <path
        v-for="(fetta, i) in spicchi"
        :key="fetta.etichetta"
        :d="fetta.tracciato"
        :fill="colore(i)"
        stroke="var(--color-surface, #fff)"
        stroke-width="2"
      >
        <title>{{ titolo(fetta) }}</title>
      </path>
    </svg>

    <!-- La legenda porta i valori, non solo i colori: gli spicchi chiari non
         arrivano a 3:1 sul bianco, e il colore da solo non deve mai essere
         l'unico modo di leggere un grafico. -->
    <ul class="torta-legenda">
      <li v-for="(fetta, i) in spicchi" :key="fetta.etichetta">
        <span class="torta-punto" :style="{ background: colore(i) }"></span>
        <span class="torta-nome">{{ fetta.etichetta }}</span>
        <span class="torta-valore">{{ formatta(fetta.valore) }}</span>
        <span class="torta-quota">{{ fetta.quota }}%</span>
      </li>
    </ul>
  </div>
</template>

<script>
import { formatPriceToPrint } from "@/utilities/formatting";

/**
 * [sc-48351] La torta di una scheda delle statistiche.
 *
 * Serve a una cosa sola: **parte su tutto, a colpo d'occhio**. Non a
 * confrontare valori vicini — per quello ci sono i numeri della legenda, che
 * stanno sempre accanto agli spicchi.
 *
 * I colori sono una scala categoriale fissa, non il colore dell'azienda: gli
 * spicchi vanno distinti fra loro anche da chi non distingue i rossi dai
 * verdi, e con un colore che cambia da cliente a cliente non si può garantire.
 * La scala è stata verificata (banda di luminosità, distanza fra spicchi
 * vicini anche in simulazione di daltonismo).
 */
const COLORI = Object.freeze([
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#71717a",
]);

export default {
  name: "PieStat",
  props: {
    /** `[{ etichetta, valore }]`, già nell'ordine in cui vanno letti. */
    fette: { type: Array, required: true },
    /** `soldi` per gli euro, `numero` per i conteggi. */
    formato: { type: String, default: "numero" },
  },
  computed: {
    totale() {
      return this.fette.reduce(
        (somma, f) => somma + (Number(f.valore) || 0),
        0
      );
    },
    /** Gli spicchi con il tracciato già calcolato e la quota in percentuale. */
    spicchi() {
      const totale = this.totale;
      if (totale <= 0) return [];
      let angolo = -Math.PI / 2; // si parte da mezzogiorno
      return this.fette
        .filter((f) => (Number(f.valore) || 0) > 0)
        .map((f) => {
          const valore = Number(f.valore) || 0;
          const ampiezza = (valore / totale) * Math.PI * 2;
          const tracciato = this.arco(angolo, angolo + ampiezza);
          angolo += ampiezza;
          return {
            etichetta: f.etichetta,
            valore,
            quota: Math.round((valore / totale) * 1000) / 10,
            tracciato,
          };
        });
    },
    descrizione() {
      return this.spicchi.map((f) => `${f.etichetta} ${f.quota}%`).join(", ");
    },
  },
  methods: {
    colore(indice) {
      return COLORI[indice % COLORI.length];
    },
    formatta(valore) {
      return this.formato === "soldi" ? formatPriceToPrint(valore) : valore;
    },
    titolo(fetta) {
      return `${fetta.etichetta}: ${this.formatta(fetta.valore)} (${
        fetta.quota
      }%)`;
    },
    /** Il tracciato di uno spicchio, dal centro e ritorno. */
    arco(da, a) {
      const raggio = 50;
      const x1 = 60 + raggio * Math.cos(da);
      const y1 = 60 + raggio * Math.sin(da);
      const x2 = 60 + raggio * Math.cos(a);
      const y2 = 60 + raggio * Math.sin(a);
      const grande = a - da > Math.PI ? 1 : 0;
      return `M 60 60 L ${x1.toFixed(2)} ${y1.toFixed(
        2
      )} A ${raggio} ${raggio} 0 ${grande} 1 ${x2.toFixed(2)} ${y2.toFixed(
        2
      )} Z`;
    },
  },
};
</script>

<style scoped>
.torta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.torta-disegno {
  width: 96px;
  height: 96px;
  flex: 0 0 auto;
}

.torta-legenda {
  margin: 0;
  padding: 0;
  list-style: none;
  flex: 1 1 140px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
}

.torta-legenda li {
  display: grid;
  grid-template-columns: 10px 1fr auto auto;
  align-items: center;
  gap: 8px;
}

.torta-punto {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

/* Le etichette vanno a capo invece di finire in puntini: dentro una scheda
   stretta «rivestimenti parete» diventava «rivest…», e una legenda che non si
   legge non è una legenda. */
.torta-nome {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--color-text-muted, #71717a);
}

.torta-unica {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-muted, #71717a);
}

.torta-unica strong {
  color: var(--color-text, #18181b);
}

.torta-valore {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-text, #18181b);
}

.torta-quota {
  min-width: 42px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted, #71717a);
}
</style>
